"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateUuid = exports.validateIPaddress = exports.validateIsoDateTime = exports.validatePhoneE164 = exports.validateEmailAddress = exports.validateUrlForJob = exports.validateUrl = void 0;
const ZonedDateTime_1 = require("../Utils/ZonedDateTime");
// import { isPossiblePhoneNumber } from "libphonenumber-js";
const Utils_1 = require("./Utils");
const ipv4rx = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const ipv6rx = /^(?:[A-F0-9]{1,4}:){7}[A-F0-9]{1,4}$/i;
function hostNameType(url) {
    if (ipv4rx.test(url.hostname)) {
        return "IPv4";
    }
    if (ipv6rx.test(url.hostname) || (/^\[.+\]$/.test(url.hostname) && ipv6rx.test(url.hostname.slice(1, -1)))) {
        return "IPv6";
    }
    return "DNS";
}
/**
 * Validate a URL string. It's up to the caller to deem the URL valid or not for its use case.
 * Has all the features that validator.js/isUrl() would provide us.
 * Tolerates the absence of protocol (e.g. "google.com").
 * @returns absentProtocol in case protocol is absent (e.g. "google.com").
 * @returns nonStandardProtocol in case protocol is non-standard (e.g. "trump://google.com").
 * @returns nonStandardTLD in case the top-level domain is non-standard (e.g. "https://google.c").
 * @returns malformedURL in case of a malformed URL, considering the acceptedHostnameTypes option.
 */
function validateUrl(str, options) {
    var _a, _b, _c;
    let ret = [];
    if (str.length > ((_a = options === null || options === void 0 ? void 0 : options.maxLength) !== null && _a !== void 0 ? _a : 2048)) {
        ret.push("urlTooLong");
        return ret;
    }
    let url;
    try {
        url = new URL(str);
        if (!url.hostname) {
            throw "prepended fake protocol and retry";
        }
        const standardProtocols = (_b = options === null || options === void 0 ? void 0 : options.standardProtocols) !== null && _b !== void 0 ? _b : ["https", "http"];
        if (!standardProtocols.map((p) => `${p}:`).includes(url.protocol)) {
            ret.push("nonStandardProtocol");
        }
        if (!["IPv4", "IPv6"].includes(hostNameType(url)) && !/\.[a-z]{2,}$/i.test(url.hostname)) {
            ret.push("nonStandardTLD");
        }
    }
    catch (err) {
        if (/[a-z][a-z0-9]*:\//i.test(str)) {
            ret.push("malformedURL");
        }
        else {
            try {
                str = `http://${str}`;
                url = new URL(str);
                ret = ret.filter((r) => r !== "nonStandardProtocol");
                ret.push("absentProtocol", ...validateUrl(str));
            }
            catch (err) {
                ret.push("malformedURL");
            }
        }
        return ret;
    }
    if (url) {
        const acceptedHostnameTypes = (_c = options === null || options === void 0 ? void 0 : options.acceptedHostnameTypes) !== null && _c !== void 0 ? _c : ["dotlessDomains", "IPv4", "IPv6"];
        let ok = true;
        switch (hostNameType(url)) {
            case "IPv4":
                ok = acceptedHostnameTypes.includes("IPv4");
                break;
            case "IPv6":
                ok = acceptedHostnameTypes.includes("IPv6");
                break;
            case "DNS":
                ok = acceptedHostnameTypes.includes("dotlessDomains") || url.hostname.includes(".");
                break;
        }
        if (!ok) {
            ret.push("malformedURL");
        }
    }
    return ret;
}
exports.validateUrl = validateUrl;
/**
 * Check whether a url is acceptable as a job url.
 */
function validateUrlForJob(url) {
    const validity = validateUrl(url);
    return (!validity.includes("malformedURL") && !validity.includes("nonStandardProtocol") && !validity.includes("urlTooLong"));
}
exports.validateUrlForJob = validateUrlForJob;
/**
 * Validate an e-mail address.
 * NB: cleanedWithoutPlus is populated only if input address contains a '+' character.
 *
 * Example:
 *   input: "foOBaR+hi+theRe@hOtmail.Com"
 *   cleaned: "foobar+hi+there@hotmail.com"
 *   domain: "hotmail.com"
 *   cleanedWithoutPlus: "foobar@hotmail.com"
 */
function validateEmailAddress(input, options) {
    const cleaned = input.toLowerCase().trim();
    const rx = /^((?:(?:(?:[a-zA-Z0-9][.\-+_]?)*)[a-zA-Z0-9][_]?)+)@((?:(?:(?:[a-zA-Z0-9][.\-_]?){0,62})[a-zA-Z0-9])+)\.([a-zA-Z0-9]{2,15})$/g;
    if (!rx.test(cleaned)) {
        return "malformed";
    }
    const lhs = (0, Utils_1.assertDefinedAndReturn)(cleaned.split("@")[0]);
    const rhs = (0, Utils_1.assertDefinedAndReturn)(cleaned.split("@")[1]);
    const hasPlusSign = lhs.includes("+");
    if ((options === null || options === void 0 ? void 0 : options.rejectPlusSign) && hasPlusSign) {
        return "malformed";
    }
    return {
        cleaned,
        domain: rhs,
        cleanedWithoutPlus: hasPlusSign ? `${(0, Utils_1.assertDefinedAndReturn)(lhs.split("+")[0])}@${rhs}` : undefined,
    };
}
exports.validateEmailAddress = validateEmailAddress;
/**
 * Validate a phone number.
 */
function validatePhoneE164(str) {
    const rx = /^\+[1-9]\d{1,14}$/;
    // const isPossibleNumber = isPossiblePhoneNumber(str);
    const isPossibleE164 = rx.test(str);
    return /*isPossibleNumber && */ isPossibleE164;
}
exports.validatePhoneE164 = validatePhoneE164;
/**
 * Validate an ISO zoned date time.
 */
function validateIsoDateTime(str) {
    return ZonedDateTime_1.ZonedDateTime.jsonParseReviver("", str) instanceof ZonedDateTime_1.ZonedDateTime;
}
exports.validateIsoDateTime = validateIsoDateTime;
/**
 * Validate an IP address.
 */
function validateIPaddress(txt) {
    const ipformat = /((^\s*((([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]))\s*$)|(^\s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))(%.+)?\s*$))/;
    return ipformat.test(txt);
}
exports.validateIPaddress = validateIPaddress;
/**
 * Validate a UUID.
 */
function validateUuid(str) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
}
exports.validateUuid = validateUuid;
//# sourceMappingURL=StringValidators.js.map